import React, { useState, useRef, useEffect } from 'react';
import { X, Search } from 'lucide-react';
import { trpc } from '@/lib/trpc';

interface Person {
  id: number;
  name: string;
  email: string;
  avatar?: string;
}

interface PeopleCellProps {
  value: any;
  config?: { maxAssignees?: number };
  onChange?: (value: string) => void;
  readOnly?: boolean;
}

export const PeopleCell: React.FC<PeopleCellProps> = ({ value, config, onChange, readOnly }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Parse value (pode ser array de IDs ou array de objetos)
  let selectedPeople: Person[] = [];
  try {
    if (typeof value === 'string') {
      const parsed = JSON.parse(value);
      selectedPeople = Array.isArray(parsed) ? parsed : [parsed];
    } else if (Array.isArray(value)) {
      selectedPeople = value;
    } else if (value) {
      selectedPeople = [value];
    }
  } catch (e) {
    // Keep empty
  }

  // Buscar usuários disponíveis
  const { data: users = [] } = trpc.auth.listUsers.useQuery();

  const filteredUsers = users.filter(
    (user: any) =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleTogglePerson = (user: any) => {
    if (readOnly) return;

    const person = { id: user.id, name: user.name, email: user.email, avatar: user.avatar };
    const isSelected = selectedPeople.some((p) => p.id === user.id);

    let newSelection: Person[];
    if (isSelected) {
      newSelection = selectedPeople.filter((p) => p.id !== user.id);
    } else {
      const maxAssignees = config?.maxAssignees || Infinity;
      if (selectedPeople.length >= maxAssignees) {
        // Substituir o último
        newSelection = [...selectedPeople.slice(0, -1), person];
      } else {
        newSelection = [...selectedPeople, person];
      }
    }

    if (onChange) {
      onChange(JSON.stringify(newSelection));
    }
  };

  const handleRemove = (personId: number) => {
    if (readOnly) return;
    const newSelection = selectedPeople.filter((p) => p.id !== personId);
    if (onChange) {
      onChange(JSON.stringify(newSelection));
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (id: number) => {
    const colors = ['#00c875', '#0086c0', '#a25ddc', '#fdab3d', '#e2445c', '#579bfc', '#ff158a'];
    return colors[id % colors.length];
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        onClick={() => !readOnly && setIsOpen(!isOpen)}
        className="flex items-center gap-1 cursor-pointer min-h-[32px] px-2"
      >
        {selectedPeople.length === 0 ? (
          <span className="text-gray-400 text-sm">-</span>
        ) : (
          <>
            {selectedPeople.slice(0, 3).map((person) => (
              <div
                key={person.id}
                className="relative group"
                title={`${person.name} (${person.email})`}
              >
                {person.avatar ? (
                  <img
                    src={person.avatar}
                    alt={person.name}
                    className="w-7 h-7 rounded-full border-2 border-white"
                  />
                ) : (
                  <div
                    className="w-7 h-7 rounded-full border-2 border-white flex items-center justify-center text-white text-xs font-medium"
                    style={{ backgroundColor: getAvatarColor(person.id) }}
                  >
                    {getInitials(person.name)}
                  </div>
                )}
                {!readOnly && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(person.id);
                    }}
                    className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3 text-white" />
                  </button>
                )}
              </div>
            ))}
            {selectedPeople.length > 3 && (
              <div className="w-7 h-7 rounded-full bg-gray-300 flex items-center justify-center text-xs font-medium text-gray-700">
                +{selectedPeople.length - 3}
              </div>
            )}
          </>
        )}
      </div>

      {isOpen && !readOnly && (
        <div className="absolute z-50 mt-1 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-2 max-h-80 overflow-hidden flex flex-col">
          <div className="px-3 pb-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search people..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>
          </div>

          <div className="overflow-y-auto">
            {filteredUsers.map((user: any) => {
              const isSelected = selectedPeople.some((p) => p.id === user.id);
              return (
                <button
                  key={user.id}
                  onClick={() => handleTogglePerson(user)}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-3"
                >
                  {user.avatar ? (
                    <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-full" />
                  ) : (
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                      style={{ backgroundColor: getAvatarColor(user.id) }}
                    >
                      {getInitials(user.name)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{user.name}</div>
                    <div className="text-xs text-gray-500 truncate">{user.email}</div>
                  </div>
                  {isSelected && (
                    <div className="w-5 h-5 rounded bg-blue-500 flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
